import { z } from 'zod';
import type { ColumnType } from './columns';
import { containerFields, oneContainer } from './pm';

/**
 * Workflows, as in monday.com: one trigger, then a list of steps - actions,
 * if/otherwise conditions and delays. The API and the builder share these
 * shapes; what a step may point at (a board, a column, a person) is checked
 * by the API against the person saving it.
 */

export const workflowTriggers = [
  'item_created', 'status_changed', 'column_changed', 'item_moved',
  'person_assigned', 'date_arrives', 'schedule',
] as const;
export const WorkflowTriggerType = z.enum(workflowTriggers);
export type WorkflowTriggerType = z.infer<typeof WorkflowTriggerType>;

export const workflowActions = [
  'set_status', 'set_column', 'assign_people', 'move_to_group', 'create_item',
  'add_update', 'notify', 'slack_post', 'archive_item',
] as const;
export const WorkflowAction = z.enum(workflowActions);
export type WorkflowAction = z.infer<typeof WorkflowAction>;

export const workflowConditions = [
  'status_is', 'column_empty', 'column_not_empty', 'person_assigned', 'in_group', 'number_compare',
] as const;
export type WorkflowConditionKind = (typeof workflowConditions)[number];

/** A scheduled workflow has no item, so only these make sense in it. */
export const ITEMLESS_ACTIONS: readonly WorkflowAction[] = ['create_item', 'notify', 'slack_post'];

/** Actions that change the item that started the run - the board's permission level applies. */
export const ITEM_EDIT_ACTIONS: readonly WorkflowAction[] = [
  'set_status', 'set_column', 'assign_people', 'move_to_group', 'archive_item',
];

/** How many workflows may set each other off in a chain. */
export const WORKFLOW_MAX_CHAIN = 3;
/** Steps one workflow may hold, and one run may take. */
export const WORKFLOW_MAX_STEPS = 50;
/** If/otherwise branches nest this deep at most. */
export const WORKFLOW_MAX_NESTING = 3;

/** Columns a status trigger, a "set status" action and a "status is" condition work with. */
export const LABEL_COLUMN_TYPES: readonly ColumnType[] = ['status', 'priority', 'dropdown'];
/** Columns "set a column value" can write. */
export const SETTABLE_COLUMN_TYPES: readonly ColumnType[] = [
  'text', 'number', 'link', 'email', 'phone', 'date', 'checkbox', 'rating', 'location',
];
export const NUMBER_COLUMN_TYPES: readonly ColumnType[] = ['number', 'rating'];
/** Columns a "column changed" trigger can watch: anything a person can type into. */
export const WATCHABLE_COLUMN_TYPES: readonly ColumnType[] = [
  'status', 'text', 'person', 'date', 'dropdown', 'number', 'checkbox', 'timeline', 'priority',
  'rating', 'link', 'email', 'phone', 'location', 'country', 'files', 'hour', 'week',
  'world_clock', 'dependency', 'color_picker', 'tags',
];

const id = z.string().uuid();
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'validation.invalid_time');
const label = z.string().min(1).max(200);
const template = z.string().max(2000);

// ── trigger ───────────────────────────────────────────────────────────────

export const WorkflowTrigger = z.discriminatedUnion('type', [
  z.object({ type: z.literal('item_created'), boardId: id, groupId: id.optional() }),
  z.object({
    type: z.literal('status_changed'), boardId: id, columnId: id,
    from: label.optional(), to: label.optional(),
  }),
  z.object({ type: z.literal('column_changed'), boardId: id, columnId: id }),
  z.object({ type: z.literal('item_moved'), boardId: id, groupId: id.optional() }),
  z.object({ type: z.literal('person_assigned'), boardId: id }),
  z.object({
    type: z.literal('date_arrives'), boardId: id, columnId: id,
    daysBefore: z.number().int().min(0).max(365).default(0), at: hhmm.default('09:00'),
  }),
  z.object({
    type: z.literal('schedule'), frequency: z.enum(['daily', 'weekly']).default('daily'),
    /** 0 = Sunday, as everywhere else in the app. */
    weekday: z.number().int().min(0).max(6).optional(), at: hhmm.default('09:00'),
  }),
]);
export type WorkflowTrigger = z.infer<typeof WorkflowTrigger>;

// ── actions ───────────────────────────────────────────────────────────────

export const workflowActionParams = {
  set_status: z.object({ columnId: id, label }),
  set_column: z.object({
    columnId: id,
    value: z.union([z.string().max(2000), z.number(), z.boolean()]).nullable().optional(),
    /** Date columns: this many days from the day the step runs. */
    days: z.number().int().min(-3650).max(3650).optional(),
  }),
  assign_people: z.object({
    userIds: z.array(id).min(1).max(50),
    mode: z.enum(['add', 'replace']).default('add'),
  }),
  move_to_group: z.object({ groupId: id }),
  create_item: z.object({
    boardId: id,
    /** The board's first group when left out. */
    groupId: id.optional(),
    name: template.default('{item}'),
    /** Copy values into the target's columns with the same name and type. */
    copyColumns: z.boolean().default(false),
  }),
  add_update: z.object({ text: template.min(1) }),
  notify: z.object({ userIds: z.array(id).min(1).max(50), message: template.min(1) }),
  slack_post: z.object({ message: template.min(1) }),
  archive_item: z.object({}),
} satisfies Record<WorkflowAction, z.ZodTypeAny>;

export type WorkflowActionParams = {
  [A in WorkflowAction]: z.infer<(typeof workflowActionParams)[A]>;
};

// ── conditions ────────────────────────────────────────────────────────────

export const WorkflowCondition = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('status_is'), columnId: id, label }),
  z.object({ kind: z.literal('column_empty'), columnId: id }),
  z.object({ kind: z.literal('column_not_empty'), columnId: id }),
  z.object({ kind: z.literal('person_assigned'), userId: id }),
  z.object({ kind: z.literal('in_group'), groupId: id }),
  z.object({
    kind: z.literal('number_compare'), columnId: id,
    op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq']), value: z.number(),
  }),
]);
export type WorkflowCondition = z.infer<typeof WorkflowCondition>;

// ── steps ─────────────────────────────────────────────────────────────────

export const delayUnits = ['minutes', 'hours', 'days'] as const;

export type WorkflowActionStep = {
  [A in WorkflowAction]: { type: 'action'; action: A; params: WorkflowActionParams[A] };
}[WorkflowAction];
export interface WorkflowConditionStep {
  type: 'condition';
  condition: WorkflowCondition;
  then: WorkflowStep[];
  else: WorkflowStep[];
}
export interface WorkflowDelayStep {
  type: 'delay';
  amount: number;
  unit: (typeof delayUnits)[number];
}
export type WorkflowStep = WorkflowActionStep | WorkflowConditionStep | WorkflowDelayStep;

const ActionStepShape = z.object({
  type: z.literal('action'),
  action: WorkflowAction,
  params: z.record(z.unknown()).default({}),
});
const ConditionStepShape = z.object({
  type: z.literal('condition'),
  condition: WorkflowCondition,
  then: z.array(z.lazy(() => WorkflowStep)).default([]),
  else: z.array(z.lazy(() => WorkflowStep)).default([]),
});
const DelayStepShape = z.object({
  type: z.literal('delay'),
  amount: z.number().int().min(1).max(400),
  unit: z.enum(delayUnits),
});

/**
 * One step. An action's params are checked against that action's own shape,
 * with errors reported at `params.<field>` so the builder can point at them.
 */
export const WorkflowStep: z.ZodType<WorkflowStep, z.ZodTypeDef, unknown> = z
  .discriminatedUnion('type', [ActionStepShape, ConditionStepShape, DelayStepShape])
  .transform((step, ctx) => {
    if (step.type !== 'action') return step as WorkflowStep;
    const parsed = workflowActionParams[step.action].safeParse(step.params);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: ['params', ...issue.path] });
      return z.NEVER;
    }
    return { ...step, params: parsed.data } as WorkflowStep;
  });

const WorkflowFields = z.object({
  folderId: id.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  isActive: z.boolean().default(false),
  trigger: WorkflowTrigger,
  steps: z.array(WorkflowStep).max(WORKFLOW_MAX_STEPS),
});

/** In a workspace, or at the organization level - one or the other. */
export const CreateWorkflow = WorkflowFields.extend(containerFields).superRefine(oneContainer);
export type CreateWorkflow = z.infer<typeof CreateWorkflow>;

export const UpdateWorkflow = WorkflowFields.partial();
export type UpdateWorkflow = z.infer<typeof UpdateWorkflow>;

// ── runs ──────────────────────────────────────────────────────────────────

export const workflowRunStatuses = ['queued', 'running', 'waiting', 'success', 'failed', 'skipped'] as const;
export type WorkflowRunStatus = (typeof workflowRunStatuses)[number];

/** Why a step did not happen: an error code and its params, translated when read. */
export interface WorkflowReason {
  code: string;
  params?: Record<string, unknown>;
}

/** One line of a run's history, as stored. */
export interface WorkflowLogEntry {
  kind: 'action' | 'condition' | 'delay';
  action?: WorkflowAction;
  result: 'done' | 'skipped' | 'failed' | 'waiting' | 'yes' | 'no';
  reason?: WorkflowReason;
  /** A delay: when the run carries on. */
  resumeAt?: string;
  at: string;
}
